import { AttributeType, LevelType } from 'data/types';
import { hasFootprintPolygons } from 'utils/footprint-polygons';
import {
  isLevelConditioned,
  levelHasRequiredLevelDetailsAttributes
} from 'utils/structure';
import {
  levelHasSpaces,
  levelRequiresSpaces
} from 'utils/structure-add-spaces';
import { getBaseLayoutOrderedLevels } from 'utils/structure-layout-order';

const PROJECT_SETTING_LABELS: Record<string, string> = {
  square_footage: 'square footage'
};

function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

function getProjectSettingValue(
  projectSettings: AttributeType[],
  code: string
): unknown {
  return projectSettings.find((ps) => ps.code === code)?.value;
}

export function getConfigureLevelDetailsMissingReason(
  structure: LevelType[]
): string | undefined {
  if (structure.length === 0) {
    return 'Add at least one floor before configuring level details';
  }
  const incompleteLevels = structure.filter(
    (level) => !levelHasRequiredLevelDetailsAttributes(level)
  );
  if (incompleteLevels.length === 1) {
    return `Complete level details for ${incompleteLevels[0].label}`;
  }
  if (incompleteLevels.length > 1) {
    return `Complete level details for ${incompleteLevels.length} floors`;
  }
  if (!structure.some((level) => isLevelConditioned(level))) {
    return 'Mark at least one floor as conditioned';
  }
  return undefined;
}

export function getConfirmDetailsMissingReason(
  projectSettings: AttributeType[],
  structureTypeId: number | null,
  structure: LevelType[]
): string | undefined {
  const missing: string[] = [];
  if (!hasValue(getProjectSettingValue(projectSettings, 'square_footage'))) {
    missing.push(PROJECT_SETTING_LABELS.square_footage);
  }
  if (missing.length === 1) {
    return `Enter the ${missing[0]} to continue`;
  }
  if (missing.length > 1) {
    return `Required fields: ${joinList(missing)}`;
  }
  if (!structure.some((level) => isLevelConditioned(level))) {
    return 'Add at least one floor to continue';
  }
  return undefined;
}

export function structureBasicsRequirementsMet(
  projectSettings: AttributeType[],
  structureTypeId: number | null,
  structure: LevelType[]
): boolean {
  return (
    getConfirmDetailsMissingReason(
      projectSettings,
      structureTypeId,
      structure
    ) === undefined
  );
}

export function getFootprintMissingReason(
  structure: LevelType[]
): string | undefined {
  if (structure.length === 0) {
    return 'Add a floor before drawing a footprint';
  }
  const incompleteLevels = structure.filter(
    (level) => !hasFootprintPolygons(level)
  );
  if (incompleteLevels.length === 0) return undefined;
  if (incompleteLevels.length === 1) {
    return `Draw a footprint with at least 3 points on ${incompleteLevels[0].label}`;
  }
  return 'Draw a footprint with at least 3 points on every floor';
}

export function getAddSpacesMissingReason(structure: LevelType[]): string {
  const incompleteLevels = getBaseLayoutOrderedLevels(structure).filter(
    (level) => levelRequiresSpaces(level) && !levelHasSpaces(level)
  );
  if (incompleteLevels.length === 1) {
    return `Add at least one space to ${incompleteLevels[0].label}`;
  }
  return 'Add at least one space to each floor that needs them';
}

const STATIC_STRUCTURE_STEP_REASONS: Record<string, string> = {
  orientation: 'Confirm the structure orientation',
  space_details: 'Review ductwork, windows and doors, and heat sources',
  sizing_tuning: 'Confirm fine-tuning settings'
};

export type StructureStepMissingReasonContext = {
  structure: LevelType[];
  projectSettings: AttributeType[];
  structureTypeId: number | null;
  isBasicSizing?: boolean;
  coolingBtu?: number;
};

export function getStructureStepMissingReason(
  stepId: string,
  context: StructureStepMissingReasonContext
): string | undefined {
  if (stepId === 'configure_level_details') {
    return getConfigureLevelDetailsMissingReason(context.structure);
  }
  if (stepId === 'confirm_details') {
    const baseReason = getConfirmDetailsMissingReason(
      context.projectSettings,
      context.structureTypeId,
      context.structure
    );
    if (baseReason) return baseReason;
    if (context.isBasicSizing) {
      if (
        !hasValue(
          getProjectSettingValue(context.projectSettings, 'insulation_level')
        )
      ) {
        return 'Choose an insulation level to continue';
      }
      if (!(Number(context.coolingBtu) > 0)) {
        return 'Wait for the system size estimate to finish';
      }
    }
    return undefined;
  }
  if (stepId === 'footprint') {
    return getFootprintMissingReason(context.structure);
  }
  if (stepId === 'add_spaces') {
    return getAddSpacesMissingReason(context.structure);
  }
  return STATIC_STRUCTURE_STEP_REASONS[stepId];
}
